import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { AppService } from '../../../store/app.service';
import { IClient } from '../../../core/models/clients.models';
import { CampaignsApiService } from '../../campaigns/campaigns.apiservice';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { TASK_PRIORITY } from '../../../core/helpers/constants';
import { createGuid } from '../../../core/helpers/utility';
import { IUser } from '../../../core/models/datafeed.models';
import { ITask } from '../../../core/models/tasks.models';
import { TasksApiService } from '../../tasks/tasks.apiservice';

@Component({
  selector: 'app-landing-page-request',
  templateUrl: './landing-page-request.component.html',
  styleUrls: ['./landing-page-request.component.scss']
})
export class LandingPageRequestComponent implements OnInit, OnDestroy {
  @Input() campaignId?: string;
  lpForm: FormGroup;
  allClients: IClient[] = [];
  campaignsData: Array<any> = [];
  allCampaigns: Array<any> = [];
  allTaskAssignees: Array<IUser> = [];

  public offerTypes: Array<{ title: string; value: string }> = [];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private fb: FormBuilder,
    private dataFeedService: DataFeedService,
    private campaignService: CampaignsApiService,
    private appService: AppService,
    public activeModal: NgbActiveModal,
    private tasksApi: TasksApiService,
    private translate: TranslateService
  ) {
    this.lpForm = this.fb.group({
      FormType: ['LP Request'],
      clientId: [''],
      campaignId: ['', Validators.required],
      lpType: ['Templated', Validators.required],
      LPTemplate: ['', Validators.required],
      LocalChanges: [''],
      CorporateChangesApproved: [false],
      offerType: ['', Validators.required],
      Offer: [''],
      BookingFlow: ['', Validators.required],
    });

    this.offerTypes = [
      { title: this.translate.instant('SUSTAINED'), value: 'Sustained' },
      { title: this.translate.instant('QUARTERLY_SUSTAINED'), value: 'Quarterly Sustained' },
      { title: this.translate.instant('PRESALE_PHASE_1'), value: 'Presale Phase 1' },
      { title: this.translate.instant('PRESALE_PHASE_2'), value: 'Presale Phase 2' },
      { title: this.translate.instant('PRESALE_PHASE_3'), value: 'Presale Phase 3' },
      { title: this.translate.instant('CUSTOM'), value: 'Custom' },
    ];

    this.dataFeedService.getAllTaskAssignees()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response?.good) {
          this.allTaskAssignees = response.data ?? [];
        }
      });
  }

  ngOnInit() {
    this.dataFeedService.getAllClients()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allClients = response.data.map((i: any) => ({ ...i, fullName: i.firstName + ' ' + i.lastName }));
        }
      });

    if (this.campaignId) {
      this.campaignService.getCampaignDetails(this.campaignId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          const campaign = response.data;
          this.allCampaigns = [campaign];
          this.campaignsData = [campaign];
          this.lpForm.get('clientId')?.setValue(campaign?.clientId ?? campaign?.client?.id ?? '');
        });
      this.lpForm.get('campaignId')?.setValue(this.campaignId);
    } else {
      this.appService.getAllCampaigns$()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          this.allCampaigns = response.data.filter((campaign: any) => {
            return !campaign.isDeleted && campaign.stage !== 'Inactive';
          });
          this.filterCampaignsByClient();
        });
      this.lpForm.get('clientId')?.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.filterCampaignsByClient();
          this.lpForm.get('campaignId')?.setValue('');
        });
      this.appService.fetchAllCampaigns({});
    }
    this.lpForm.get('lpType')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(lpType => this.updateCorporateApprovalValidator(lpType));
    this.updateCorporateApprovalValidator(this.lpForm.get('lpType')?.value);
  }

  filterCampaignsByClient() {
    const clientId = this.lpForm.get('clientId')?.value;
    if (clientId) {
      this.campaignsData = this.allCampaigns.filter((c: any) => c.clientId === clientId);
    } else {
      this.campaignsData = [...this.allCampaigns];
    }
  }

  onSubmit() {
    if (this.lpForm.invalid) {
      this.lpForm.markAllAsTouched();
      return;
    }

    const values = this.lpForm.getRawValue();
    const assignee = this.findTaskAssignee('sean', 'oh') ?? this.findTaskAssignee('lance', 'williams');
    const campaign = this.allCampaigns.find(item => item?.id === values.campaignId) ??
      this.campaignsData.find(item => item?.id === values.campaignId);
    if (!assignee?.id) {
      console.error('Could not find task assignee Sean Oh.');
      return;
    }
    const clientId = campaign?.clientId ?? campaign?.client?.id ?? values.clientId ?? null;

    const description = [
      `LP Template: ${values.LPTemplate ?? ''}`,
      '',
      `Offer Type: ${values.offerType ?? ''}`,
      `Offer: ${values.Offer ?? ''}`,
      `Enable Booking Flow? ${values.BookingFlow ?? ''}`,
      ...(values.lpType === 'Templated with Local Changes'
        ? ['', 'Local Changes:', values.LocalChanges ?? '', '', 'I have verified these changes are approved with Corporate']
        : [])
    ].join('\n');
    const dueDate = new Date();
    dueDate.setHours(17, 0, 0, 0);
    const dueDateValue = `${dueDate.getFullYear()}-${String(dueDate.getMonth() + 1).padStart(2, '0')}-${String(dueDate.getDate()).padStart(2, '0')}T17:00:00.000Z`;
    const task: ITask = {
      id: createGuid(),
      title: `LP Request - ${values.lpType}`,
      priority: TASK_PRIORITY.find(priority => priority.title === 'TASK_PRIORITY.ESSENTIAL')?.value ?? 2,
      description,
      campaignId: values.campaignId || null,
      campaignName: campaign?.name ?? '',
      clientId,
      dueDate: dueDateValue,
      completedOn: null,
      isCompleted: false,
      assignedUserId: assignee.id,
      assignedUserName: `${assignee.firstName} ${assignee.lastName}`.trim()
    };

    this.lpForm.disable();
    this.tasksApi.saveTask(task)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: response => this.activeModal.close(response),
        error: error => {
          console.error('Error creating landing page request task:', error);
          this.lpForm.enable();
        }
      });
  }

  private findTaskAssignee(firstName: string, lastName: string): IUser | undefined {
    return this.allTaskAssignees.find(user =>
      (user.firstName ?? '').trim().toLowerCase() === firstName &&
      (user.lastName ?? '').trim().toLowerCase() === lastName
    );
  }

  private updateCorporateApprovalValidator(lpType: string) {
    const approvalControl = this.lpForm.get('CorporateChangesApproved');
    if (!approvalControl) {
      return;
    }

    if (lpType === 'Templated with Local Changes') {
      approvalControl.setValidators(Validators.requiredTrue);
    } else {
      approvalControl.clearValidators();
      approvalControl.setValue(false, { emitEvent: false });
    }
    approvalControl.updateValueAndValidity({ emitEvent: false });
  }

  close() {
    this.activeModal.dismiss();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
